import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, ClipboardList } from "lucide-react";
import AuthShell, { AUTH_PRIMARY_BUTTON_CLASS, AUTH_SECONDARY_BUTTON_CLASS, AUTH_TITLE_CLASS, AUTH_LABEL_CLASS, AuthLoadingIndicator } from "@/components/AuthShell";
import SurveiSkala from "@/components/SurveiSkala";
import SurveiPendahuluan from "@/components/SurveiPendahuluan";
import SurveiDemografiKepnakes from "@/components/SurveiDemografiKepnakes";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { surveiApi, formatApiErrorDetail } from "@/lib/api";
import {
    jumlahTerjawab, totalButir, persenProgres, butirBelumDijawab, semuaTerjawab, susunJawaban, labelProdi,
    panjangTeks, pesanKeadaan, TEKS_TAUTAN_TIDAK_VALID, angkaPendahuluan, adaJawabanAngka, PESAN_MINIMAL_ANGKA, tbButir,
    teksLegendaTb, KELOMPOK_KEPNAKES, paramKelompok, PENDAHULUAN_KEPNAKES, demografiBelumLengkap, isiDemografiKirim,
    sudahDikirimDariPerangkat, tandaiDikirimDariPerangkat, TEKS_SUDAH_DIKIRIM_PERANGKAT, namaBagianKepnakes,
} from "@/lib/survei";

// Halaman PUBLIK Survei Kepuasan Peserta Didik (tanpa login/sidebar). Jawaban & token HANYA di memori halaman
// (keputusan pemilik: tidak di localStorage/sessionStorage) -- muat ulang/tutup tab = isian hilang (diperingatkan).
const PESAN_JARINGAN = "Tidak dapat terhubung ke server. Periksa koneksi internet Anda lalu coba lagi.";

const galatDari = (e) => {
    if (e?.response?.status === 404) return TEKS_TAUTAN_TIDAK_VALID; // kode tautan tidak dikenal
    return e?.response ? formatApiErrorDetail(e.response.data?.detail) : PESAN_JARINGAN;
};

const keAtas = () => {
    try { window.scrollTo({ top: 0 }); } catch { /* lingkungan tanpa scrollTo */ }
};

function Judul({ children }) {
    return (
        <h1 className={`${AUTH_TITLE_CLASS} flex items-center gap-2 text-xl`}>
            <ClipboardList aria-hidden="true" className="h-6 w-6 shrink-0" /> {children}
        </h1>
    );
}

function Galat({ teks }) {
    if (!teks) return null;
    return <div role="alert" data-testid="survei-galat" className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">{teks}</div>;
}

// Tombol di layar >= 768px (md): lebar menyesuaikan isi & rata kanan (tidak melebar penuh di kartu lebar); di bawah
// 768px tetap penuh seperti semula.
const TOMBOL_MD = "md:w-auto md:min-w-[11rem] md:px-8";

// Bilah kemajuan. >= 768px (md): menempel di atas (sticky) selama menggulir & menampilkan "Bagian X dari <jumlah bagian>
// · N dari <jumlah butir> terisi" (angka dari versi butir periode; full-bleed di dalam kartu); di bawah 768px tampilan lama.
function Progres({ jawaban, dimensi, bagian }) {
    const persen = persenProgres(jawaban, dimensi);
    const terisi = `${jumlahTerjawab(jawaban)} dari ${totalButir(dimensi)}`;
    return (
        <div className="space-y-1 md:sticky md:top-0 md:z-10 md:-mx-10 md:bg-card md:px-10 md:py-3 md:shadow-[0_1px_0_hsl(var(--border))] lg:-mx-14 lg:px-14" data-testid="survei-progres">
            <div className="flex justify-between text-xs text-muted-foreground">
                <span className="md:hidden">{jumlahTerjawab(jawaban)} dari {totalButir(dimensi)} pernyataan terjawab</span>
                <span className="hidden md:inline" data-testid="survei-progres-md">{bagian ? `${bagian} · ` : ""}{terisi} terisi</span>
                <span>{persen}%</span>
            </div>
            <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={persen} aria-label="Kemajuan pengisian"
                 className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-[#00B9AD] transition-all" style={{ width: `${persen}%` }} />
            </div>
        </div>
    );
}

// Sesi tiruan mode pratinjau (tanpa token; tidak pernah dikirim ke server).
const SESI_PRATINJAU = { token: null, periode: "Pratinjau", prodi: "(pratinjau)", jenjang: "Sp1", tahap: null };

// `kode` = kode tautan periode dari alamat /survei-peserta-didik-<kode> (RuteSisa); semua panggilan API memakai periode itu.
// `pratinjau` (editor butir Super Admin, PR #108) = butir bentuk respons /butir dari draf: formulir yg SAMA tampil tanpa
// memanggil API sama sekali (status/butir/verifikasi/kirim), tombol kirim nonaktif berlabel "Pratinjau".
// `kelompok` (PR #112) = "kepnakes" dari alamat /survei-keperawatan-nakes-<kode>: TANPA verifikasi NPM/tanggal lahir
// (tahap "pembuka" -> POST /mulai menerbitkan token sesi sekali pakai), butir & isian terbuka SAMA, lalu langkah
// "demografi" (Institusi, Program Studi, Rentang Usia, Jenis Kelamin bila ditanyakan) sebelum kirim. Bawaan = Kedokteran
// (alur lama, tidak berubah).
export default function SurveiPublik({ kode, pratinjau = null, kelompok = "kedokteran" }) {
    const kepnakes = kelompok === KELOMPOK_KEPNAKES && !pratinjau;
    const [tahap, setTahap] = useState("muat");
    const [info, setInfo] = useState("");
    const [butir, setButir] = useState(null);
    const [npm, setNpm] = useState("");
    const [lahir, setLahir] = useState("");
    const [sesi, setSesi] = useState(null);
    const [jawaban, setJawaban] = useState({});
    const [halaman, setHalaman] = useState(0);
    const [teks, setTeks] = useState({ positif: "", negatif: "" });
    const [sorot, setSorot] = useState(null);
    const [galat, setGalat] = useState("");
    const [sibuk, setSibuk] = useState(false);
    const [pilihan, setPilihan] = useState(null); // Kepnakes: pilihan demografi dari server
    const [demo, setDemo] = useState({});
    const [diPerangkat, setDiPerangkat] = useState(false);

    useEffect(() => {
        if (pratinjau) { setButir(pratinjau); setTahap("verifikasi"); return; }
        const cfg = paramKelompok(kelompok);
        Promise.all([surveiApi.get(`/survei/publik/${kode}/status`, cfg), surveiApi.get(`/survei/publik/${kode}/butir`, cfg),
            ...(kelompok === KELOMPOK_KEPNAKES ? [surveiApi.get(`/survei/publik/${kode}/pilihan`)] : [])])
            .then(([s, b, pl]) => {
                setButir(b.data);
                if (pl) { setPilihan(pl.data); setDiPerangkat(sudahDikirimDariPerangkat(kode)); }
                if (s.data.keadaan === "buka") setTahap(pl ? "pembuka" : "verifikasi");
                else { setInfo(pesanKeadaan(s.data.keadaan, s.data.periode)); setTahap("info"); }
            })
            .catch((e) => { setInfo(galatDari(e)); setTahap("info"); });
    }, [kode, pratinjau, kelompok]);

    const sedangMengisi = !pratinjau && (["konfirmasi", "isi", "terbuka", "demografi"].includes(tahap) || (tahap === "verifikasi" && jumlahTerjawab(jawaban) > 0));
    useEffect(() => {
        if (!sedangMengisi) return undefined;
        const peringatkan = (ev) => { ev.preventDefault(); ev.returnValue = ""; };
        window.addEventListener("beforeunload", peringatkan);
        return () => window.removeEventListener("beforeunload", peringatkan);
    }, [sedangMengisi]);

    useEffect(keAtas, [tahap, halaman]);

    const dimensi = useMemo(() => butir?.dimensi || [], [butir]);
    // Nomor tampil 1..N berurutan menurut urutan dimensi (nomor asli butir boleh melompat, mis. JP 37,38,40,41).
    const awalHalaman = useMemo(() => dimensi.map((_, i) => dimensi.slice(0, i).reduce((n, d) => n + d.butir.length, 0)), [dimensi]);

    const verifikasi = async (ev) => {
        ev.preventDefault();
        setGalat("");
        if (pratinjau) { setSesi(SESI_PRATINJAU); setTahap("konfirmasi"); return; } // tanpa API
        if (!npm.trim() || !lahir) { setGalat("NPM dan tanggal lahir wajib diisi."); return; }
        setSibuk(true);
        try {
            const res = await surveiApi.post(`/survei/publik/${kode}/verifikasi`, { npm: npm.trim(), tanggal_lahir: lahir });
            setSesi(res.data);
            setNpm(""); setLahir("");  // tidak disimpan lebih lama dari perlu
            setTahap("konfirmasi");
        } catch (e) {
            if (e?.response?.status === 409) { setInfo(formatApiErrorDetail(e.response.data?.detail)); setTahap("sudah"); }
            else setGalat(galatDari(e));
        } finally {
            setSibuk(false);
        }
    };

    // Kepnakes: token sesi tanpa verifikasi identitas (POST /mulai; tidak menulis apa pun di server).
    const mintaSesiKepnakes = async () => (await surveiApi.post(`/survei/publik/${kode}/mulai`)).data;
    const mulaiKepnakes = async () => {
        setGalat(""); setSibuk(true);
        try {
            setSesi(await mintaSesiKepnakes());
            setTahap(semuaTerjawab(dimensi, jawaban) ? "terbuka" : "isi");
        } catch (e) {
            if ([400, 404, 503].includes(e?.response?.status)) { setInfo(galatDari(e)); setTahap("info"); }
            else setGalat(galatDari(e));
        } finally {
            setSibuk(false);
        }
    };
    const keDemografi = () => {
        setGalat("");
        if (!adaJawabanAngka(jawaban)) { setGalat(PESAN_MINIMAL_ANGKA); return; }
        setTahap("demografi");
    };

    const bukanSaya = () => { setSesi(null); setGalat(""); setTahap("verifikasi"); };
    const mulai = () => { setGalat(""); setTahap(semuaTerjawab(dimensi, jawaban) ? "terbuka" : "isi"); };

    const pilih = useCallback((no, v) => {
        setJawaban((j) => ({ ...j, [no]: v }));
        setSorot((s) => (s === no ? null : s));
    }, []);

    const berikutnya = () => {
        const belum = butirBelumDijawab(dimensi[halaman], jawaban);
        if (belum !== null) {
            setSorot(belum);
            setGalat("Masih ada pernyataan yang belum dijawab di bagian ini.");
            document.getElementById(`butir-${belum}`)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
            return;
        }
        setGalat(""); setSorot(null);
        if (halaman < dimensi.length - 1) setHalaman(halaman + 1);
        else setTahap("terbuka");
    };

    const sebelumnya = () => {
        setGalat(""); setSorot(null);
        if (tahap === "terbuka") { setHalaman(dimensi.length - 1); setTahap("isi"); }
        else if (tahap === "demografi") setTahap("terbuka");
        else if (halaman > 0) setHalaman(halaman - 1);
    };

    const kirimKepnakes = async () => {
        setGalat("");
        const belum = demografiBelumLengkap(demo, pilihan);
        if (belum.length) { setGalat(`Pilih ${belum.join(", ")}.`); return; }
        setSibuk(true);
        try {
            await surveiApi.post(`/survei/publik/${kode}/kirim`,
                { jawaban: susunJawaban(dimensi, jawaban), ...teks, ...isiDemografiKirim(demo, pilihan) },
                { headers: { Authorization: `Bearer ${sesi.token}` }, ...paramKelompok(kelompok) });
            tandaiDikirimDariPerangkat(kode);
            setJawaban({}); setTeks({ positif: "", negatif: "" }); setDemo({}); setSesi(null); setHalaman(0);
            setTahap("selesai");
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409) { setInfo(formatApiErrorDetail(e.response.data?.detail)); setJawaban({}); setSesi(null); setTahap("sudah"); }
            else if (status === 401) {
                // Sesi kedaluwarsa: minta token baru; jawaban tetap di halaman, responden cukup menekan Kirim lagi.
                try {
                    setSesi(await mintaSesiKepnakes());
                    setGalat("Sesi pengisian sudah diperbarui. Tekan KIRIM sekali lagi; jawaban Anda masih tersimpan di halaman ini.");
                } catch (e2) { setInfo(galatDari(e2)); setTahap("info"); }
            } else if (status === 400 || status === 404 || status === 503) { setInfo(galatDari(e)); setTahap("info"); }
            else setGalat(galatDari(e));
        } finally {
            setSibuk(false);
        }
    };

    const kirim = async () => {
        if (pratinjau) return; // mode pratinjau tidak pernah mengirim jawaban
        if (kepnakes) { await kirimKepnakes(); return; }
        setGalat("");
        if (!adaJawabanAngka(jawaban)) { setGalat(PESAN_MINIMAL_ANGKA); return; }
        setSibuk(true);
        try {
            await surveiApi.post(`/survei/publik/${kode}/kirim`, { jawaban: susunJawaban(dimensi, jawaban), ...teks },
                { headers: { Authorization: `Bearer ${sesi.token}` } });
            setJawaban({}); setTeks({ positif: "", negatif: "" }); setSesi(null); setHalaman(0);
            setTahap("selesai");
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409) { setInfo(formatApiErrorDetail(e.response.data?.detail)); setJawaban({}); setSesi(null); setTahap("sudah"); }
            else if (status === 401) {
                setSesi(null); setTahap("verifikasi");
                setGalat("Sesi pengisian sudah berakhir. Verifikasi ulang; jawaban Anda masih tersimpan di halaman ini.");
            } else if (status === 400 || status === 404 || status === 503) { setInfo(galatDari(e)); setTahap("info"); }
            else setGalat(galatDari(e));
        } finally {
            setSibuk(false);
        }
    };

    return (
        <AuthShell lebar>
            <div className="space-y-5" data-testid={`survei-tahap-${tahap}`}>
                <Judul>Survei Kepuasan Peserta Didik</Judul>
                {pratinjau && (
                    <div role="note" data-testid="survei-mode-pratinjau" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                        Mode pratinjau: isian tidak diverifikasi dan jawaban tidak dikirim.
                    </div>
                )}

                {tahap === "muat" && <p className="text-sm text-muted-foreground">Memuat survei…</p>}

                {["info", "sudah", "selesai"].includes(tahap) && (
                    <div className="space-y-3 text-sm text-foreground" data-testid="survei-info">
                        {tahap === "selesai" && (
                            <p className="flex items-start gap-2 font-semibold text-emerald-700 dark:text-emerald-400">
                                <CheckCircle2 aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
                                Jawaban Anda sudah terkirim. Terima kasih atas partisipasi Anda.
                            </p>
                        )}
                        {tahap !== "selesai" && <p>{info}</p>}
                    </div>
                )}

                {tahap === "pembuka" && (
                    <div className="space-y-4" data-testid="survei-pembuka-kepnakes">
                        <SurveiPendahuluan angka={butir && angkaPendahuluan(butir)} paragraf={PENDAHULUAN_KEPNAKES} />
                        {diPerangkat && (
                            <div role="status" data-testid="survei-sudah-di-perangkat" className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100">
                                {TEKS_SUDAH_DIKIRIM_PERANGKAT}
                            </div>
                        )}
                        <Galat teks={galat} />
                        <p className="text-xs text-muted-foreground">Jawaban belum tersimpan sampai Anda menekan "Kirim". Jangan memuat ulang atau menutup halaman ini selama mengisi.</p>
                        <button type="button" onClick={mulaiKepnakes} disabled={sibuk} aria-busy={sibuk} className={`${AUTH_PRIMARY_BUTTON_CLASS} flex items-center justify-center md:ml-auto md:w-fit md:min-w-[14rem] md:px-10`} data-testid="survei-mulai">
                            {sibuk ? <AuthLoadingIndicator label="Memuat" /> : "MULAI MENGISI"}
                        </button>
                    </div>
                )}

                {tahap === "verifikasi" && (
                    <form onSubmit={verifikasi} className="space-y-4" noValidate>
                        <SurveiPendahuluan angka={butir && angkaPendahuluan(butir)} />
                        <Galat teks={galat} />
                        <div className="space-y-4 md:grid md:grid-cols-2 md:gap-6 md:space-y-0">
                            <div className="space-y-1.5">
                                <label htmlFor="survei-npm" className={AUTH_LABEL_CLASS}>NPM</label>
                                <Input id="survei-npm" data-testid="survei-npm" value={npm} onChange={(e) => setNpm(e.target.value)}
                                       inputMode="numeric" autoComplete="off" maxLength={64} className="h-12 text-base" />
                            </div>
                            <div className="space-y-1.5">
                                <label htmlFor="survei-lahir" className={AUTH_LABEL_CLASS}>Tanggal lahir</label>
                                <Input id="survei-lahir" data-testid="survei-lahir" type="date" value={lahir} onChange={(e) => setLahir(e.target.value)}
                                       autoComplete="off" className="h-12 text-base" />
                            </div>
                        </div>
                        <button type="submit" disabled={sibuk} aria-busy={sibuk} className={`${AUTH_PRIMARY_BUTTON_CLASS} flex items-center justify-center md:ml-auto md:w-fit md:min-w-[14rem] md:px-10`} data-testid="survei-verifikasi">
                            {sibuk ? <AuthLoadingIndicator label="Memeriksa" /> : "LANJUTKAN"}
                        </button>
                    </form>
                )}

                {tahap === "konfirmasi" && sesi && (
                    <div className="space-y-4" data-testid="survei-konfirmasi">
                        <p className="text-sm text-foreground">Pastikan data berikut sesuai dengan Anda:</p>
                        <dl className="space-y-2 rounded-lg bg-muted p-4 text-sm md:grid md:grid-cols-3 md:gap-6 md:space-y-0">
                            <div><dt className="text-muted-foreground">{labelProdi(sesi.jenjang)}</dt><dd className="font-semibold">{sesi.prodi}</dd></div>
                            <div><dt className="text-muted-foreground">Jenjang</dt><dd className="font-semibold">{sesi.jenjang}</dd></div>
                            {sesi.tahap && <div><dt className="text-muted-foreground">Tahap</dt><dd className="font-semibold">{sesi.tahap}</dd></div>}
                        </dl>
                        <p className="text-xs text-muted-foreground">Jawaban belum tersimpan sampai Anda menekan "Kirim". Jangan memuat ulang atau menutup halaman ini selama mengisi.</p>
                        <div className="space-y-4 md:flex md:flex-row-reverse md:justify-start md:gap-4 md:space-y-0">
                            <button type="button" onClick={mulai} className={`${AUTH_PRIMARY_BUTTON_CLASS} ${TOMBOL_MD}`} data-testid="survei-mulai">MULAI MENGISI</button>
                            <button type="button" onClick={bukanSaya} className={`${AUTH_SECONDARY_BUTTON_CLASS} ${TOMBOL_MD}`}>Bukan data saya</button>
                        </div>
                    </div>
                )}

                {tahap === "isi" && dimensi[halaman] && (
                    <div className="space-y-4">
                        <Progres jawaban={jawaban} dimensi={dimensi} bagian={`Bagian ${halaman + 1} dari ${dimensi.length}`} />
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-testid="survei-bagian">
                                Bagian {halaman + 1} dari {dimensi.length}
                            </div>
                            <h2 className="text-lg font-bold text-foreground">{kepnakes ? namaBagianKepnakes(dimensi[halaman].nama) : dimensi[halaman].nama}</h2>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {butir.skala.min} = {butir.skala.label_min} · {butir.skala.maks} = {butir.skala.label_maks}
                            </p>
                            {dimensi[halaman].butir.some((b) => tbButir(butir, b)) && (
                                <p className="mt-0.5 text-xs text-muted-foreground" data-testid="survei-legenda-tb">{teksLegendaTb(butir.opsi_tb)}</p>
                            )}
                        </div>
                        <div className="space-y-3">
                            {dimensi[halaman].butir.map((b, i) => (
                                <SurveiSkala key={b.no} butir={b} urutan={awalHalaman[halaman] + i + 1} skala={butir.skala}
                                             nilai={jawaban[b.no]} onPilih={pilih} disorot={sorot === b.no} tb={tbButir(butir, b)} />
                            ))}
                        </div>
                        <Galat teks={galat} />
                        <div className="grid grid-cols-2 gap-3 md:flex md:justify-between md:gap-4">
                            <button type="button" onClick={sebelumnya} disabled={halaman === 0} className={`${AUTH_SECONDARY_BUTTON_CLASS} ${TOMBOL_MD} disabled:opacity-40`} data-testid="survei-sebelumnya">Sebelumnya</button>
                            <button type="button" onClick={berikutnya} className={`${AUTH_PRIMARY_BUTTON_CLASS} ${TOMBOL_MD}`} data-testid="survei-berikutnya">Berikutnya</button>
                        </div>
                    </div>
                )}

                {tahap === "terbuka" && (
                    <div className="space-y-4">
                        <Progres jawaban={jawaban} dimensi={dimensi} bagian="Isian terbuka" />
                        <p className="text-sm text-foreground">Dua pertanyaan terakhir ini boleh dikosongkan. Hindari menuliskan nama atau data pribadi.</p>
                        {butir.terbuka.map((t) => (
                            <div key={t.kunci} className="space-y-1.5">
                                <label htmlFor={`survei-${t.kunci}`} className="text-sm font-semibold text-foreground">{t.teks}</label>
                                <Textarea id={`survei-${t.kunci}`} data-testid={`survei-${t.kunci}`} rows={4} maxLength={butir.teks_maks}
                                          value={teks[t.kunci]} onChange={(e) => setTeks((x) => ({ ...x, [t.kunci]: e.target.value }))} />
                                <div className="text-right text-xs text-muted-foreground">{panjangTeks(teks[t.kunci])}/{butir.teks_maks}</div>
                            </div>
                        ))}
                        <Galat teks={galat} />
                        <div className="grid grid-cols-2 gap-3 md:flex md:justify-between md:gap-4">
                            <button type="button" onClick={sebelumnya} className={`${AUTH_SECONDARY_BUTTON_CLASS} ${TOMBOL_MD}`}>Sebelumnya</button>
                            {kepnakes ? (
                                <button type="button" onClick={keDemografi} className={`${AUTH_PRIMARY_BUTTON_CLASS} ${TOMBOL_MD}`} data-testid="survei-ke-demografi">Berikutnya</button>
                            ) : (
                                <button type="button" onClick={kirim} disabled={sibuk || !!pratinjau} aria-busy={sibuk} className={`${AUTH_PRIMARY_BUTTON_CLASS} ${TOMBOL_MD} flex items-center justify-center`} data-testid="survei-kirim">
                                    {pratinjau ? "PRATINJAU" : sibuk ? <AuthLoadingIndicator label="Mengirim" /> : "KIRIM"}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {tahap === "demografi" && pilihan && (
                    <div className="space-y-4">
                        <Progres jawaban={jawaban} dimensi={dimensi} bagian="Data responden" />
                        <SurveiDemografiKepnakes pilihan={pilihan} nilai={demo} onUbah={(f) => { setGalat(""); setDemo(f); }} />
                        <Galat teks={galat} />
                        <div className="grid grid-cols-2 gap-3 md:flex md:justify-between md:gap-4">
                            <button type="button" onClick={sebelumnya} className={`${AUTH_SECONDARY_BUTTON_CLASS} ${TOMBOL_MD}`}>Sebelumnya</button>
                            <button type="button" onClick={kirim} disabled={sibuk} aria-busy={sibuk} className={`${AUTH_PRIMARY_BUTTON_CLASS} ${TOMBOL_MD} flex items-center justify-center`} data-testid="survei-kirim">
                                {sibuk ? <AuthLoadingIndicator label="Mengirim" /> : "KIRIM"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </AuthShell>
    );
}
